<script setup lang="ts">
import { tpgIconUpload } from '@tpg/grammar-web-icons';
import { computed, ref } from 'vue';

import {
  GrammarPrimaryButton,
  GrammarQuaternaryButton,
} from '@nora-monorepo/grammar-ui';

import { useDraftDrawLayerCreation } from '#nora-ui/map/composables/useDraftDrawLayerCreation';
import { useMapStore } from '#nora-ui/map/stores/map-store';

const props = defineProps<{
  mapId: string;
}>();

const { mapId } = props;

const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(mapId);

const { importGeoJsonFiles, showCreateDraftLayerPrompt } = useDraftDrawLayerCreation();
const uploadDrawLayerInput = ref<HTMLInputElement>();

// Draft layers belong to an operation, so creating one requires an active operation as target
const canCreateDraftLayer = computed(() => {
  return map.ref.activeOperationId !== undefined;
});

function createDraftLayer(): void {
  showCreateDraftLayerPrompt({
    map,
    operationId: map.ref.activeOperationId,
  });
}

function uploadDrawLayer(event: Event): void {
  const input = event.target;

  if (!(input instanceof HTMLInputElement) || input.files === null) {
    return;
  }

  importGeoJsonFiles(input.files, {
    map,
    operationId: map.ref.activeOperationId,
  });

  input.value = '';
}
</script>

<template>
  <div class="bg-light-bg-primary dark:bg-dark-bg-primary flex justify-between items-center gap-4 p-2">
    <GrammarPrimaryButton
      v-can="{
        permission: 'create:draw-layer',
      }"
      label="New private layer"
      class="grow"
      :disabled="!canCreateDraftLayer"
      :title="canCreateDraftLayer ? undefined : 'Select an operation first'"
      @click="() => createDraftLayer()"
    />
    <div>
      <input
        ref="uploadDrawLayerInput"
        type="file"
        accept=".geojson,application/geo+json"
        class="hidden"
        @change="uploadDrawLayer"
      >

      <GrammarQuaternaryButton
        v-can="{
          permission: 'create:draw-layer',
        }"
        size="small"
        :icon-template="tpgIconUpload"
        title="Upload private layer"
        :disabled="!canCreateDraftLayer"
        @click="() => uploadDrawLayerInput?.click()"
      />
    </div>
  </div>
</template>
